import { faTag as faTagRegular } from '@fortawesome/pro-regular-svg-icons';
import { faTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Text, TextWithIcon } from '@modusign/design-system';
import { getGetLabelQueryKey, getLabel } from '@modusign/openapi-client-category';
import { MouseEventHandler } from 'react';
import { useQueries } from 'react-query';

import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

interface LabelListSummaryProps {
  labelIds: string[];
  fontSize?: 'sm' | 'md';
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

export default function LabelListSummary({
  labelIds,
  fontSize = 'sm',
  onClick,
}: LabelListSummaryProps) {
  const workspaceId = useWorkspaceId();

  const queries = useQueries(
    labelIds.map((labelId) => ({
      queryKey: getGetLabelQueryKey(workspaceId, labelId),
      queryFn: () => getLabel(workspaceId, labelId),
      enabled: !!(labelId && workspaceId),
    }))
  );

  const labelNames = queries.map((query) => query?.data?.name).filter((q): q is string => !!q);

  if (!labelNames.length) {
    return (
      <TextWithIcon
        color="textNeutralLight"
        fontSize={fontSize}
        leftIcon={<FontAwesomeIcon icon={faTagRegular} style={{ fontSize: '12px' }} />}
      >
        라벨 없음
      </TextWithIcon>
    );
  }

  return (
    <Text
      as={onClick && 'button'}
      color="textNeutralLight"
      display="flex"
      fontSize={fontSize}
      sx={{
        alignItems: 'center',
        maxWidth: '100%',

        ...(onClick
          ? {
              '&:hover': {
                textDecoration: 'underline',
                textUnderlineOffset: '0.2em',
                color: 'textNeutralDark',
                svg: {
                  color: 'iconNeutralDark',
                },
              },
            }
          : undefined),
      }}
      type={onClick && 'button'}
      onClick={onClick}
    >
      <Box
        as={FontAwesomeIcon}
        color="iconNeutralBase"
        flex="none"
        icon={faTag}
        style={{ fontSize: '12px', width: '18px' }}
      />
      <Box
        as="span"
        flexGrow={0}
        flexShrink={1}
        sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
      >
        {labelNames[0]}
      </Box>
      {labelNames.length > 1 && (
        <Box as="span" flex="none" whiteSpace="pre">
          외 {labelNames.length - 1}개
        </Box>
      )}
    </Text>
  );
}
